import React, { useRef, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaOtpInput, { type KuikaOtpInputStatus } from "../../../../ui-kit/kuika-otp-input/kuika-otp-input";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

// The code this demo accepts; anything else is refused and shakes the row.
const DEMO_CODE: string = "482913";

// Type or paste a code. A complete code other than DEMO_CODE turns the field red and shakes it; the
// next edit clears the error, so the next wrong code shakes again. "Resend" empties it and puts the
// caret back, through the ref, as the change-password screen does after sending a new code.
const VerifyDemo: React.FC = (): JSX.Element => {
  const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState<string>("");
  const [status, setStatus] = useState<KuikaOtpInputStatus>("default");
  const [verified, setVerified] = useState<boolean>(false);

  const handleChange = (next: string): void => {
    setCode(next);
    setStatus("default");
    setVerified(false);
  };

  const handleComplete = (next: string): void => {
    if (next === DEMO_CODE) {
      setVerified(true);
      return;
    }
    setStatus("error");
  };

  const handleResend = (): void => {
    handleChange("");
    inputRef.current?.focus();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-s)", alignItems: "flex-start" }}>
      <KuikaOtpInput
        ref={inputRef}
        value={code}
        status={status}
        onChange={handleChange}
        onComplete={handleComplete}
        dataTestId="otpInputDemo"
      />
      <div style={{ display: "flex", gap: "var(--gap-s)", alignItems: "center" }}>
        <KuikaButton type="secondary" size="sm" onClick={handleResend}>
          Resend
        </KuikaButton>
        <span>{verified ? "Verified" : `Try pasting ${DEMO_CODE}`}</span>
      </div>
    </div>
  );
};

export const OtpInputStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="OTP Input"
      componentName="KuikaOtpInput"
      sourcePath="ui-kit/kuika-otp-input"
      description="One-time code field: one real input (code autofill, paste, screen readers see a single field) over a row of cells. Digits roll in, a pasted code fills its cells one after another, the focus ring slides between cells, and an error shakes the row once."
    >
      <SpecRow
        label="Verify"
        caption={`type or paste — ${DEMO_CODE} is accepted, anything else is refused`}
        align="start"
      >
        <VerifyDemo />
      </SpecRow>
      <SpecRow label="Filled">
        <KuikaOtpInput defaultValue="731" />
      </SpecRow>
      <SpecRow label="Four digits">
        <KuikaOtpInput length={4} />
      </SpecRow>
      <SpecRow label="Error" caption="as it starts — no shake on first render">
        <KuikaOtpInput defaultValue="111111" status="error" />
      </SpecRow>
      <SpecRow label="Disabled">
        <KuikaOtpInput defaultValue="482" disabled />
      </SpecRow>
    </ShowcaseFrame>
  );
};
